import { numberOfAccessPoints } from '../businessWifi/businessWifiFieldNames';
import { addProductVulcan } from '../customerInformation/customerInformationFieldNames';
import { fibreSpeed, offers } from '../fibeInternet/fibreInternetFieldNames';
import {
  mobilityMonthlyCharge,
  mobilityOneTimeCharge
} from '../mobility/mobilityFieldNames';
import {
  secureBusinessOffersVulcan,
  secureBusinessPlan
} from '../secureBusiness/secureBusinessFieldNames';

import {
  keyControlIncludedEquipmentVulcan,
  keyProtectIncludedEquipmentVulcan,
  keyMonitorIncludedEquipmentVulcan,
  keyControlIncludedEquipmentCameraSelectionVulcan,
  keyMonitorAdditionalEquipmentVulcan,
  keyProtectAdditionalEquipmentVulcan,
  keyControlAdditionalEquipmentVulcan,
  keyProtectIncludedEquipmentCameraSelectionVulcan,
  isCouponBasedDiscountField
} from '../../../utils/constant';

import { getFieldValue } from '../../utils';
import { getLocale } from '../../../misc';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import { cloneDeep } from 'lodash';

export const GET_FIELD_VALUE = 'GET_FIELD_VALUE';

const getLocalizedConfig = (locale, forceOpposite) => {
  const isEnglish = locale === 'en-US';
  return (forceOpposite ? !isEnglish : isEnglish) ? FR : EN;
};

const getSummaryConfig = alternateLanguage => {
  const locale = getLocale();

  const {
    vulcanWebform: {
      yes,
      vulMonitorTSB,
      vulControlTSB,
      vulProtectTSB,
      vulMobility,
      vulTSB,
      vulFibreInternet,
      vulBusinessWifi,
      summaryConfig: {
        speedLabel,
        offersLabel,
        devicesLabel,
        planLabel,
        includedLabel,
        additionalAccessPointsLabel,
        additionalEquipmentsFiveYearLabel,
        additionalEquipmentsOneTimeLabel,
        additionalEquipmentsThreeYearLabel,
        additionalEquipmentsMonthlyRecuringLabel,
        installationTSBLabel
      }
    }
  } = getLocalizedConfig(locale, !alternateLanguage);

  const includedEquipmentValuesMap = {
    [vulMonitorTSB]: [keyMonitorIncludedEquipmentVulcan],
    [vulProtectTSB]: [
      keyProtectIncludedEquipmentVulcan,
      keyProtectIncludedEquipmentCameraSelectionVulcan
    ],
    [vulControlTSB]: [
      keyControlIncludedEquipmentVulcan,
      keyControlIncludedEquipmentCameraSelectionVulcan
    ]
  };

  const additionalEquipmentValuesMap = {
    [vulMonitorTSB]: [keyMonitorAdditionalEquipmentVulcan],
    [vulProtectTSB]: [keyProtectAdditionalEquipmentVulcan],
    [vulControlTSB]: [keyControlAdditionalEquipmentVulcan]
  };

  return [
    {
      sectionTitle: 'Fibre Internet',
      shouldShowSection: formFields => {
        return getFieldValue(addProductVulcan, formFields).includes(
          vulFibreInternet
        );
      },
      rows: [
        {
          label: speedLabel,
          description: GET_FIELD_VALUE,
          checkForKey: fibreSpeed
        },
        {
          label: offersLabel,
          listSelectedOptionsForKey: offers,
          getCustomLabel: formField => {
            const updatedFormField = cloneDeep(formField);
            if (updatedFormField[offers].value) {
              updatedFormField[offers].value = updatedFormField[
                offers
              ].value.map(discount => discount.replace(/\s\((BTL|ATL)\)/g, ''));
              updatedFormField[offers].options = updatedFormField[
                offers
              ].options.map(option => {
                const updatedOption = { ...option };
                if (updatedOption.fields.value) {
                  updatedOption.fields.value = updatedOption.fields.value.replace(
                    /\s\((BTL|ATL)\)/g,
                    ''
                  );
                }
                return updatedOption;
              });
            }
            return updatedFormField[offers];
          }
        },
        {
          label: 'Installation',
          listSelectedOptionsForKey: 'fibreInstallation'
        }
      ]
    },
    {
      sectionTitle: 'Business Wifi',
      shouldShowSection: formFields => {
        return getFieldValue(addProductVulcan, formFields).includes(
          vulBusinessWifi
        );
      },
      rows: [
        {
          label: devicesLabel,
          description: 'MR28',
          checkForKey: addProductVulcan,
          checkForOption: vulBusinessWifi
        },
        {
          label: additionalAccessPointsLabel,
          checkForKey: numberOfAccessPoints,
          transformer: value => {
            const qty = +(value.qty || 0);
            const monthlyChargesPerUnit = value.monthlyCharges / qty;
            return {
              ...value,
              qty: qty - 1,
              monthlyCharges: monthlyChargesPerUnit * (qty - 1)
            };
          }
        }
      ]
    },
    {
      sectionTitle: 'Secure Business',
      shouldShowSection: formFields => {
        return getFieldValue(addProductVulcan, formFields).includes(vulTSB);
      },
      rows: [
        {
          label: planLabel,
          description: GET_FIELD_VALUE,
          checkForKey: secureBusinessPlan,
          filterKeySubstringNegativeValues: ["oneTimeCharges"]
        },
        {
          label: includedLabel,
          listSelectedValuesForEquipment: {
            basedOnKey: secureBusinessPlan,
            map: includedEquipmentValuesMap
          }
        },
        {
          label: additionalEquipmentsOneTimeLabel,
          listSelectedValuesForEquipment: {
            basedOnKey: secureBusinessPlan,
            map: additionalEquipmentValuesMap,
            filterKeysBySubstringNegative: ['easyThreeYear', 'easyFiveYear', 'businessActivityAnalytics', 'temperatureMonitoringFiveDollarVulcan', 'temperatureMonitoringTenDollarVulcan']
          }
        },
        {
          label: additionalEquipmentsMonthlyRecuringLabel,
          listSelectedValuesForEquipment: {
            basedOnKey: secureBusinessPlan,
            map: additionalEquipmentValuesMap,
            filterKeysBySubstring: ['businessActivityAnalytics', 'temperatureMonitoringFiveDollarVulcan', 'temperatureMonitoringTenDollarVulcan']
          }
        },
        {
          label: additionalEquipmentsThreeYearLabel,
          listSelectedValuesForEquipment: {
            basedOnKey: secureBusinessPlan,
            map: additionalEquipmentValuesMap,
            filterKeysBySubstring: 'easyThreeYear'
          }
        },
        {
          label: additionalEquipmentsFiveYearLabel,
          listSelectedValuesForEquipment: {
            basedOnKey: secureBusinessPlan,
            map: additionalEquipmentValuesMap,
            filterKeysBySubstring: 'easyFiveYear'
          }
        },
        {
          label: installationTSBLabel,
          description: "Installation Fee",
          checkForKey: secureBusinessPlan,
          filterKeySubstringNegativeValues: ["monthlyCharges", "fiveYearEasyPaymentCharges", "qty", "threeYearEasyPaymentCharges"]
        },
        {
          label: offersLabel,
          listSelectedOptionsForKey: secureBusinessOffersVulcan,
          getCustomLabel: formField => {
            const updatedFormField = cloneDeep(formField);
        
            if (
              updatedFormField[secureBusinessOffersVulcan] &&
              updatedFormField[secureBusinessOffersVulcan].options
            ) {
              const availableOptions = updatedFormField[secureBusinessOffersVulcan].options.map(opt => ({
                label: opt.fields.label,
                value: opt.fields.value,
                oneTimePrice: opt.fields.oneTimePrice !== undefined ? opt.fields.oneTimePrice.toString() : "",
              }));
        
              updatedFormField[secureBusinessOffersVulcan].options = updatedFormField[
                secureBusinessOffersVulcan
              ].options.map(option => ({
                ...option,
                fields: {
                  ...option.fields,
                  value: option.fields.label,
                },
              }));
        
              updatedFormField[secureBusinessOffersVulcan].value = updatedFormField[
                secureBusinessOffersVulcan
              ].value.map(selectedValue => {
                let matchedOption = availableOptions.find(
                  opt =>
                    opt.label.trim().toLowerCase() === selectedValue.trim().toLowerCase() ||
                    opt.value.trim().toLowerCase() === selectedValue.trim().toLowerCase()
                );

                if (!matchedOption) {
                  const numericValue = selectedValue.replace(/[^0-9]/g, "");
                  matchedOption = availableOptions.find(
                    opt => opt.oneTimePrice && opt.oneTimePrice.replace(/[^0-9]/g, "") === numericValue
                  );
                }
        
                return matchedOption ? matchedOption.label : selectedValue;
              });
            }
        
            return updatedFormField[secureBusinessOffersVulcan];
          }
        }
      ]
    },
    {
      sectionTitle: 'Mobility',
      shouldShowSection: formFields => {
        return getFieldValue(addProductVulcan, formFields).includes(
          vulMobility
        );
      },
      rows: [
        {
          label: 'Mobility Monthly Charge',
          checkForKey: mobilityMonthlyCharge
        },
        {
          label: 'Mobility One Time Charge',
          checkForKey: mobilityOneTimeCharge
        }
      ]
    },
    {
      sectionTitle: 'Coupon Based Discounts',
      shouldShowSection: formFields => {
        return getFieldValue(isCouponBasedDiscountField, formFields) === yes;
      },
      rows: [
        {
          label: 'redeemOfferData',
          listSelectedOptionsCouponCode: 'redeemOfferData'
        }
      ]
    }
  ];
};

export default getSummaryConfig;
